import { getErrorMsg } from 'app/core/errors/error-utils';
import { RootState } from 'app/core/redux/configure-store';
import { NotUndefined, Opt } from 'app/tools/general.types';
import { useEffectOnce } from 'app/tools/hooks/misc-hooks';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Observable, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { ApiError, isKeyedAxiosError } from '../api-core.types';
import { ApiSlice } from './api-slice-factory';

type RequestOpts = {
  fresh?: boolean;
};

type RequestHook<T, D> = (opts?: RequestOpts) => {
  request$: (data: D) => Observable<NotUndefined<T>>;
  result: T;
  sending: boolean;
  error: Opt<ApiError>;
  clearError: React.MutableRefObject<() => void>;
};

type ApiState = RootState['api'];
type Names = keyof ApiState;
type Value<N extends Names> = ApiState[N]['value'];

const apiRequestHookFactory = <N extends Names, D = void>(
  slice: ApiSlice<Value<N>, N>,
  $source: (data: D) => Observable<NotUndefined<Value<N>>>
): RequestHook<Value<N>, D> => {
  const { actions, name } = slice;

  const useRequest = (opts?: RequestOpts) => {
    const dispatch = useDispatch();
    const apiStore = useSelector(state => state.api[name]) || {};

    const [initialised, setInitialised] = useState(!opts?.fresh);
    const [errInitialised, setErrInitialised] = useState(false);

    useEffect(() => {
      if (apiStore.value === undefined && !initialised) {
        setInitialised(true);
      }
      if (apiStore.error === undefined && !errInitialised) {
        setErrInitialised(true);
      }
    }, [apiStore.error, apiStore.value, errInitialised, initialised]);

    const result = initialised ? apiStore.value : undefined;
    const error = errInitialised ? apiStore.error : undefined;

    useEffectOnce(() => {
      if (opts?.fresh) dispatch(actions.set(undefined));
      dispatch(actions.clearErrors());
    });

    const request$ = useCallback(
      (data: D) =>
        of(data).pipe(
          tap(() => dispatch(actions.start())),
          tap(() => dispatch(actions.clearErrors())),
          // switchMap = good practice even in 1-long data streams,
          // to remove any risk of requests backlogging
          switchMap($source),
          tap(v => dispatch(actions.success<Value<N>>(v))),
          catchError((e: unknown) => {
            const errorRtn = isKeyedAxiosError(e)
              ? e.data
              : ({ detail: getErrorMsg(e) } as ApiError);

            dispatch(actions.failure(errorRtn));
            // failure -> is a request, so clear last result storage
            dispatch(actions.set(undefined));
            throw e;
          }),
          finalize(() => dispatch(actions.end()))
        ),
      [dispatch]
    );

    const clearError = useRef(() => {
      dispatch(actions.clearErrors());
    });

    return useMemo(
      () => ({
        request$,
        result,
        sending: !!apiStore.loading,
        error,
        clearError,
      }),
      [apiStore.loading, error, request$, result]
    );
  };

  return useRequest;
};

export default apiRequestHookFactory;
